/**
 * Tenant Context - React Context for Multi-tenancy State Management
 *
 * Provides tenant information and context switching capabilities
 * throughout the application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { safeGetItem } from '../utils/safeStorage';
import {
  TenantConfig,
  getTenantConfig,
  initializeTenantConfig,
  setSuperAdminTenantContext,
  getSuperAdminTenantContext,
  clearSuperAdminTenantContext,
  buildTenantApiUrl,
  getEffectiveApiBaseUrl,
} from '../config/tenant.config';
import logger from '../utils/logger';

interface Tenant {
  id: number;
  name: string;
  slug: string;
  email?: string;
  plan?: string;
  is_active?: boolean;
}

interface TenantContextType {
  // Current tenant configuration
  tenantConfig: TenantConfig;

  // API base URL (respects super admin context)
  apiBaseUrl: string;

  // Super admin context switching
  isSuperAdminMode: boolean;
  selectedTenantSlug: string | null;
  availableTenants: Tenant[];

  // Actions
  switchToTenant: (tenantSlug: string) => Promise<void>;
  switchToSystemView: () => void;
  refreshTenantList: () => Promise<void>;

  // Loading states
  isLoading: boolean;
  isSwitching: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState<TenantConfig>(() => initializeTenantConfig());
  const [apiBaseUrl, setApiBaseUrl] = useState<string>(() => getEffectiveApiBaseUrl());
  const [selectedTenantSlug, setSelectedTenantSlug] = useState<string | null>(() => getSuperAdminTenantContext());
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  // Check if we're in super admin mode (viewing a specific tenant)
  const isSuperAdminMode = selectedTenantSlug !== null;

  /**
   * Fetch available tenants for super admin dropdown
   */
  const refreshTenantList = useCallback(async () => {
    try {
      const token = safeGetItem('access_token');
      if (!token) return;

      const response = await fetch(`${tenantConfig.apiBaseUrl}/api/tenants`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const tenants = await response.json();
        setAvailableTenants(tenants);
      }
    } catch (error) {
      logger.error('Failed to fetch tenant list:', error);
    }
  }, [tenantConfig.apiBaseUrl]);

  /**
   * Switch super admin context to a specific tenant
   */
  const switchToTenant = useCallback(async (tenantSlug: string) => {
    setIsSwitching(true);

    try {
      // Store the context
      setSuperAdminTenantContext(tenantSlug);

      // Update state
      setSelectedTenantSlug(tenantSlug);
      setApiBaseUrl(buildTenantApiUrl(tenantSlug));

      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('tenantContextChanged', {
        detail: { tenantSlug, apiBaseUrl: buildTenantApiUrl(tenantSlug) }
      }));

      logger.log(`[Tenant Context] Switched to tenant: ${tenantSlug}`);
    } finally {
      setIsSwitching(false);
    }
  }, []);

  /**
   * Return to system-wide view (clear tenant context)
   */
  const switchToSystemView = useCallback(() => {
    clearSuperAdminTenantContext();
    setSelectedTenantSlug(null);
    setApiBaseUrl(tenantConfig.apiBaseUrl);

    // Dispatch event
    window.dispatchEvent(new CustomEvent('tenantContextChanged', {
      detail: { tenantSlug: null, apiBaseUrl: tenantConfig.apiBaseUrl }
    }));

    logger.log('[Tenant Context] Switched to system view');
  }, [tenantConfig.apiBaseUrl]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      // Restore super admin context if it exists
      const savedContext = getSuperAdminTenantContext();
      if (savedContext) {
        setSelectedTenantSlug(savedContext);
        setApiBaseUrl(buildTenantApiUrl(savedContext));
      }

      // Fetch available tenants
      await refreshTenantList();

      setIsLoading(false);
    };

    initialize();
  }, [refreshTenantList]);

  /**
   * Listen for tenant configuration changes (e.g., from subdomain changes)
   */
  useEffect(() => {
    const handleConfigChange = () => {
      const newConfig = getTenantConfig();
      setTenantConfig(newConfig);

      if (!selectedTenantSlug) {
        setApiBaseUrl(newConfig.apiBaseUrl);
      }
    };

    window.addEventListener('popstate', handleConfigChange);
    return () => window.removeEventListener('popstate', handleConfigChange);
  }, [selectedTenantSlug]);

  const value: TenantContextType = {
    tenantConfig,
    apiBaseUrl,
    isSuperAdminMode,
    selectedTenantSlug,
    availableTenants,
    switchToTenant,
    switchToSystemView,
    refreshTenantList,
    isLoading,
    isSwitching,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
};

export default TenantContext;
